import React from 'react';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useUserProfileData } from '@/hooks/useUserProfileData';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { useTaskPopup } from '@/contexts/TaskPopupContext';
import { Button } from '@/components/ui/button';
import { Shield, Crown, User as UserIcon, Eye, ClipboardList, Clock } from 'lucide-react';

interface UserHoverCardProps {
  squadMemberId?: string;
  brandId?: string;
  children: React.ReactNode;
}

export const UserHoverCard: React.FC<UserHoverCardProps> = ({ 
  squadMemberId, 
  brandId, 
  children 
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const { data: profile, isLoading } = useUserProfileData(squadMemberId, brandId);
  const navigate = useNavigate();
  const { openTaskPopup } = useTaskPopup();

  if (!squadMemberId || !brandId) {
    return <>{children}</>;
  }

  const getRoleIcon = (role: string) => {
    const roleLower = role.toLowerCase();
    if (roleLower === 'owner') return <Crown className="h-3.5 w-3.5 text-yellow-500" />;
    if (roleLower === 'admin') return <Shield className="h-3.5 w-3.5 text-blue-500" />;
    if (roleLower === 'viewer') return <Eye className="h-3.5 w-3.5 text-gray-500" />;
    return <UserIcon className="h-3.5 w-3.5 text-green-500" />;
  };

  const getStatusIcon = (status: string) => {
    if (status === 'pending' || status === 'to-do') return '🟡';
    if (status === 'in-progress' || status === 'ongoing') return '🔵';
    if (status === 'done' || status === 'completed') return '✅';
    return '⚪';
  };

  const handleTaskClick = (task: any) => {
    setIsOpen(false);
    openTaskPopup(
      {
        ...(task as any),
        id: task.id,
        title: task.title ?? task.name,
        status: task.status ?? task.task_status,
      } as any,
      task.type
    );
  };

  const handleViewAllTasks = () => {
    setIsOpen(false);
    navigate(`/my-tasks?assignee=${squadMemberId}`);
  };

  const handleViewProfile = () => {
    setIsOpen(false);
    navigate(`/team/member/${squadMemberId}`);
  };

  return (
    <HoverCard open={isOpen} onOpenChange={setIsOpen} openDelay={300} closeDelay={100}>
      <HoverCardTrigger asChild>
        <span 
          className="cursor-pointer hover:underline decoration-dotted"
          onMouseDown={(e) => {
            // Allow click events to bubble up to parent Select trigger
            // Only prevent default if we want to stop the hover card from closing
          }}
        >
          {children}
        </span>
      </HoverCardTrigger>
      <HoverCardContent className="w-96 p-4" align="start">
        {isLoading ? (
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Skeleton className="h-12 w-12 rounded-full" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-48" />
              </div>
            </div>
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : profile ? (
          <div className="space-y-3">
            {/* Header */}
            <div className="flex items-start gap-3">
              <Avatar className="h-12 w-12">
                {profile.avatar && (
                  <AvatarImage src={profile.avatar} alt={profile.name} />
                )}
                <AvatarFallback className="bg-gradient-to-br from-primary/20 to-primary/40 text-primary font-semibold">
                  {profile.alias || profile.name.slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h4 className="font-semibold text-sm truncate">{profile.name}</h4>
                  <Badge variant="outline" className="text-xs shrink-0">
                    {profile.type}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground truncate">
                  @{profile.alias} • {profile.email}
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0"
                onClick={handleViewProfile}
                title="View profile"
              >
                <UserIcon className="h-4 w-4" />
              </Button>
            </div>

            {/* Role */}
            <div className="flex items-center gap-2 text-sm">
              <div className="flex items-center gap-1.5 text-muted-foreground">
                {getRoleIcon(profile.role)}
                <span>Role:</span>
              </div>
              <span className="font-medium">{profile.role}</span>
            </div>

            {/* Tasks */}
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <ClipboardList className="h-3.5 w-3.5" />
                  <span>Tasks:</span>
                </div>
                <span className="text-xs">
                  Pending: {profile.tasks.pending} • In Progress: {profile.tasks.in_progress} • Done: {profile.tasks.done}
                </span>
              </div>
              
              {profile.tasks.recent.length > 0 && (
                <div className="space-y-1.5 pt-2 border-t">
                  <p className="text-xs font-medium text-muted-foreground">Recent Active Tasks:</p>
                  {profile.tasks.recent.map((task) => (
                    <div
                      key={task.id}
                      onClick={() => handleTaskClick(task)}
                      className="flex items-center gap-2 text-xs hover:bg-accent/50 p-1.5 rounded cursor-pointer transition-colors"
                    >
                      <span>{getStatusIcon(task.status)}</span>
                      <span className="truncate flex-1">{task.title}</span>
                    </div>
                  ))}
                  <div className="flex items-center gap-2 pt-1">
                    <button
                      onClick={handleViewAllTasks}
                      className="text-xs text-primary hover:underline flex-1 text-left"
                    >
                      View all tasks →
                    </button>
                    <button
                      onClick={handleViewProfile}
                      className="text-xs text-primary hover:underline"
                    >
                      View profile
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Recent Activity */}
            {profile.recentActivity.length > 0 && (
              <div className="space-y-1.5 pt-2 border-t">
                <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <Clock className="h-3.5 w-3.5" />
                  <span>Recent Activity</span>
                </p>
                {profile.recentActivity.map((activity) => (
                  <div key={activity.id} className="text-xs text-muted-foreground">
                    • {activity.user_action} -{' '}
                    {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="text-sm text-muted-foreground">
            Unable to load user profile
          </div>
        )}
      </HoverCardContent>
    </HoverCard>
  );
};
